import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import api from "../../../services/api";
import { useIntl } from "../../../contexts/IntlContext";
import { useCountry } from "../../../contexts/CountryContext";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { Establishment } from "../../../types/establishment";

interface EstablishmentAutocompleteProps {
  value: Establishment[];
  onChange: (establishments: Establishment[]) => void;
  label?: string;
  helperText?: string;
  required?: boolean;
  sx?: object;
  searchOnMount?: boolean;
}

export function EstablishmentAutocomplete({
  value,
  onChange,
  label,
  helperText,
  required = false,
  sx,
  searchOnMount = false
}: EstablishmentAutocompleteProps) {
  const { country } = useCountry();
  const intl = useIntl();
  
  const defaultLabel = label || intl.formatMessage({ id: 'common.establishments' });
  const defaultHelperText = helperText || intl.formatMessage({ id: 'common.establishmentsHelper' });
  
  const [searchEstablishments, setSearchEstablishments] = useState<Establishment[]>([]);
  const [searchInput, setSearchInput] = useState("");

  const memoizedSearchEstablishments = useMemo(() => {
    const selectedIds = new Set(value.map(item => item.establishmentId));
    const filteredSearch = searchEstablishments.filter(item => !selectedIds.has(item.establishmentId));
    return filteredSearch;
  }, [searchEstablishments, value]);

  const getOptionLabel = useCallback((option: Establishment) => 
    `${option.name} - ${option.channels}`, []
  );

  const handleEstablishmentChange = useCallback((_: any, newValue: Establishment[]) => {
    const seen = new Set();
    const uniqueEstablishments = newValue.filter(item => {
      if (seen.has(item.establishmentId)) return false;
      seen.add(item.establishmentId);
      return true;
    });
    onChange(uniqueEstablishments);
  }, [onChange]);

  const fetchEstablishments = useCallback(async (search?: string) => {
    try {
      const response = await api.get("/app/establishments", {
        params: {
          limit: 20,
          offset: 0,
          country,
          ...(search && { search }),
        },
      });
      setSearchEstablishments(response.data.data);
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'establishmentAutocomplete.searchError' }));
    }
  }, [country, intl]);

  const handleInputChange = useCallback((_: any, newInputValue: string) => {
    setSearchInput(newInputValue);
  }, []);

  useEffect(() => {
    if (searchInput.trim().length < 3) return;
    const delayDebounce = setTimeout(() => fetchEstablishments(searchInput.trim()), 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput, fetchEstablishments]);

  useEffect(() => {
    if (searchOnMount) fetchEstablishments();
  }, [searchOnMount, fetchEstablishments]);

  return (
    <Autocomplete
      multiple
      options={memoizedSearchEstablishments}
      getOptionLabel={getOptionLabel}
      value={value}
      onChange={handleEstablishmentChange}
      onInputChange={handleInputChange}
      filterSelectedOptions
      disableCloseOnSelect
      renderInput={(params) => (
        <TextField
          {...params}
          label={defaultLabel}
          required={required}
          helperText={defaultHelperText}
          size="small"
          sx={sx}
        />
      )}
    />
  );
}